// src/utils/seo.js
// 轻量 SEO 工具：管理 <head> 的 meta 标签与 JSON-LD。
// 不依赖任何第三方库，兼容当前 Vue2 + 客户端渲染（CSR）架构。
// 配套文档见：docs/seo-collaboration.md（需后端/运维配合的部分）

const TITLE_SUFFIX = '衣创社区'
const TITLE_SEP = ' - '

function getHead() {
  return document.getElementsByTagName('head')[0]
}

// 获取或创建指定属性的 meta 标签，并写入 content（空值则跳过，避免写入空 meta）
function upsertMeta(attr, key, content) {
  if (content == null || content === '') return
  let el = document.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    getHead().appendChild(el)
  }
  el.setAttribute('content', String(content))
}

function upsertLink(rel, href) {
  if (!href) return
  let el = document.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    getHead().appendChild(el)
  }
  el.setAttribute('href', href)
}

// 拼接「页面标题 - 站点名」；若标题本身已以站点名结尾（如首页/列表页已自带品牌词），
// 则不再重复追加，避免出现「衣创社区 - 衣创社区」这类重复。
function withSuffix(t) {
  const s = t && String(t).trim() ? String(t).trim() : ''
  if (!s) return TITLE_SUFFIX
  if (s.endsWith(TITLE_SUFFIX)) return s
  return `${s}${TITLE_SEP}${TITLE_SUFFIX}`
}

// 设置标题（自动追加站点名，幂等避免重复）
export function setTitle(title) {
  document.title = withSuffix(title)
}

// 设置页面基础 meta：description / keywords / canonical / Open Graph / Twitter
export function setMeta(opts = {}) {
  const {
    title,
    description,
    keywords,
    canonical,
    image,
    type = 'website',
    noindex,
  } = opts

  if (title !== undefined) setTitle(title)

  if (description) upsertMeta('name', 'description', description)
  if (keywords) upsertMeta('name', 'keywords', keywords)

  // robots：noindex 页面写 noindex,nofollow；其余确保恢复为可索引（避免上一页残留）
  const robotsEl = document.querySelector('meta[name="robots"]')
  if (noindex) {
    upsertMeta('name', 'robots', 'noindex, nofollow')
  } else if (robotsEl) {
    robotsEl.setAttribute('content', 'index, follow')
  }

  if (canonical) upsertLink('canonical', canonical)

  const fullTitle = withSuffix(title)
  upsertMeta('property', 'og:title', fullTitle)
  if (description) upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:type', type)
  upsertMeta('property', 'og:site_name', TITLE_SUFFIX)
  if (image) upsertMeta('property', 'og:image', image)
  if (canonical) upsertMeta('property', 'og:url', canonical)

  upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
  upsertMeta('name', 'twitter:title', fullTitle)
  if (description) upsertMeta('name', 'twitter:description', description)
  if (image) upsertMeta('name', 'twitter:image', image)

  notifySeoReady()
}

// ---- JSON-LD 管理（按名称分块，全局块常驻，页面块随路由切换）----
const schemas = {} // name -> object | null
let jsonLdEl = null

function stripEmpty(obj) {
  if (Array.isArray(obj)) {
    return obj.map(stripEmpty).filter((v) => v !== undefined && v !== null && v !== '')
  }
  if (obj && typeof obj === 'object') {
    const out = {}
    Object.keys(obj).forEach((k) => {
      const v = stripEmpty(obj[k])
      if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
        out[k] = v
      }
    })
    return out
  }
  return obj
}

function renderJsonLd() {
  const graph = Object.keys(schemas)
    .map((k) => schemas[k])
    .filter(Boolean)
    .map(stripEmpty)
  if (!graph.length) {
    removeJsonLd()
    return
  }
  if (!jsonLdEl) {
    jsonLdEl = document.createElement('script')
    jsonLdEl.type = 'application/ld+json'
    jsonLdEl.id = 'seo-jsonld'
    getHead().appendChild(jsonLdEl)
  }
  jsonLdEl.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
}

// 设置一个命名 JSON-LD 块；data 为 null 表示移除该块
export function setSchema(name, data) {
  schemas[name] = data || null
  renderJsonLd()
  notifySeoReady()
}

// 清除 JSON-LD 块；不传 name 则清空除 organization/website 外的所有块
export function clearSchema(name) {
  if (name) {
    delete schemas[name]
  } else {
    Object.keys(schemas).forEach((k) => {
      if (k !== 'organization' && k !== 'website') delete schemas[k]
    })
  }
  renderJsonLd()
}

export function removeJsonLd() {
  if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl)
  jsonLdEl = null
}

// 当前站点 origin（用于拼接绝对 URL / canonical），CSR 下以真实访问域为准
export function getOrigin() {
  return (typeof window !== 'undefined' && window.location && window.location.origin) || ''
}

// 通知预渲染器：本页 SEO（title / meta / JSON-LD）已注入完成，可以截图快照。
// 仅在 prerender 构建（headless 浏览器）下被监听；普通运行无副作用。
export function notifySeoReady() {
  try {
    if (typeof document !== 'undefined' && document.dispatchEvent) {
      document.dispatchEvent(new Event('seo-ready'))
    }
  } catch (e) {
    /* 忽略：非浏览器环境或事件系统未就绪 */
  }
}
